import { ReactNode } from "react";

import { TTrans } from "~/hooks/useTrans";
import { CONTACT_LINK, DOCUMENTATION_MENU_LINK, ROUTES, ROUTE_PARAMS, TRANSFER_VERSION } from "~/const";
import { IUserState, TInternalUserRole } from "~/providers/interfaces";

// ikony
import HomeIcon from "~/icons/HomeIcon";
import DocumentationIcon from "~/icons/DocumentationIcon";
import GraphIcon from "~/icons/GraphIcon";
import UsersIcon from "~/icons/UsersIcon";
import ExternalIcon from "~/icons/ExternalIcon";
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ReportIcon from '@mui/icons-material/Report';
import StoreIcon from '@mui/icons-material/Store';
import BarChartIcon from '@mui/icons-material/BarChart';
import FilterIcon from '@mui/icons-material/Filter';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import QuickreplyIcon from '@mui/icons-material/Quickreply';

// ikony material design
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyIcon from '@mui/icons-material/Key';
import SettingsIcon from '@mui/icons-material/Settings';
import LanIcon from '@mui/icons-material/Lan';

export function replaceUrl(url: string, replaceObj?: { [key: string]: string }): string {
	let output = url;
	const keys = Object.keys(replaceObj);

	keys.sort((aItem, bItem) => bItem.length - aItem.length);
	keys.forEach(key => {
		output = output.replace(new RegExp(key, "gu"), replaceObj[key]);
	});

	return output;
}

export function getRoute(routePath: string): string {
	return `/${routePath}`;
}

export function checkRoute(pathStr: string): boolean {
	return Object.values(ROUTES).filter(value => (!value.test && value.path.indexOf(pathStr) !== -1) || (value.test && pathStr.match(value.test))).length === 1;
}

function getProjectPath(source: string, projectId: string, usp?: URLSearchParams) {
	return `/${replaceUrl(source, {
		[ROUTE_PARAMS.PROJECT_ID]: projectId,
	})}${usp ? `?${usp.toString()}` : ""}`;
}

interface IGetLinkProjectData {
	projectId: number;
}

interface IGetLinkConsumptionDetail extends IGetLinkProjectData {
	fromDate: string;
	tillDate: string;
}

interface IGetLinkData {
	"projects": {};
	"transfer": {};
	"admin-projects": {
		projectId?: number;
		keyword?: string;
	};
	"admin-users": {};
	"admin-services": {};
	"admin-transactions": {};
	"admin-reports": {};
	"admin-graph-reports": {};
	"admin-billing-export": {};
	"admin-catalogs": {};
	"admin-api-users": {};
	"admin-quick-comment": {};
	"project-keys": IGetLinkProjectData;
	"project-users": IGetLinkProjectData;
	"project-consumption-history": IGetLinkProjectData;
	"project-consumption-detail": IGetLinkConsumptionDetail;
	"project-settings": IGetLinkProjectData;
	"project-financial": IGetLinkProjectData;
	"project-dashboard": IGetLinkProjectData;
	"project-business": IGetLinkProjectData;
	"project-history": IGetLinkProjectData;
	"project-notifications": IGetLinkProjectData;
	"project-catalog": IGetLinkProjectData;
}

export function getLink<T extends keyof IGetLinkData>(page: T, isInternal?: boolean, data?: IGetLinkData[T]): string {
	switch (page) {
		case "projects":
			return getRoute(ROUTES.MY_PROJECTS.path);

		case "transfer":
			return getRoute(ROUTES.TRANSFER.path);

		case "admin-projects": {
			const typedData = data as IGetLinkData["admin-projects"];
			const searchParams = new URLSearchParams();

			if (typedData?.projectId) {
				searchParams.append("id", typedData.projectId.toString());
			}

			if (typedData?.keyword) {
				searchParams.append("kw", typedData.keyword);
			}

			const searchUrl = searchParams.toString();

			return `/${ROUTES.ADMIN_PROJECTS.path}${searchUrl ? `?${searchUrl}` : ""}`;
		}

		case "admin-users":
			return `/${ROUTES.ADMIN_USERS.path}`;

		case "admin-services":
			return `/${ROUTES.ADMIN_SERVICES.path}`;

		case "admin-transactions":
			return `/${ROUTES.ADMIN_TRANSACTIONS.path}`;

		case "admin-reports":
			return `/${ROUTES.ADMIN_REPORTS.path}`;

		case "admin-graph-reports":
			return `/${ROUTES.ADMIN_GRAPH_REPORTS.path}`;

		case "admin-billing-export":
			return `/${ROUTES.ADMIN_BILLING_EXPORT.path}`;

		case "admin-catalogs":
			return `/${ROUTES.ADMIN_CATALOGS.path}`;

		case "admin-quick-comment":
			return `/${ROUTES.ADMIN_QUICK_COMMENT.path}`;

		case "admin-api-users":
			return `/${ROUTES.ADMIN_API_USERS.path}`;

		case "project-keys": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_KEYS.path : ROUTES.CLIENT_PROJECT_KEYS.path;
			const typedData = data as IGetLinkData["project-keys"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-users": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_USERS.path : ROUTES.CLIENT_PROJECT_USERS.path;
			const typedData = data as IGetLinkData["project-users"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-consumption-history": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_CONSUMPTION_HISTORY.path : ROUTES.CLIENT_PROJECT_CONSUMPTION_HISTORY.path;
			const typedData = data as IGetLinkData["project-consumption-history"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-consumption-detail": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_CONSUMPTION_DETAIL.path : ROUTES.CLIENT_PROJECT_CONSUMPTION_DETAIL.path;
			const typedData = data as IGetLinkData["project-consumption-detail"];
			const usp = new URLSearchParams();

			usp.append("fromDate", typedData.fromDate);
			usp.append("tillDate", typedData.tillDate);

			return getProjectPath(source, typedData.projectId.toString(), usp);
		}

		case "project-settings": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_SETTINGS.path : ROUTES.CLIENT_PROJECT_SETTINGS.path;
			const typedData = data as IGetLinkData["project-settings"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-financial": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_FINANCIAL.path : ROUTES.CLIENT_PROJECT_FINANCIAL.path;
			const typedData = data as IGetLinkData["project-settings"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-dashboard": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_DASHBOARD.path : ROUTES.CLIENT_PROJECT_DASHBOARD.path;
			const typedData = data as IGetLinkData["project-settings"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		case "project-history": {
			const typedData = data as IGetLinkData["project-settings"];

			return getProjectPath(ROUTES.ADMIN_PROJECT_JOURNAL.path, typedData.projectId.toString());
		}

		case "project-notifications": {
			const typedData = data as IGetLinkData["project-settings"];

			return getProjectPath(ROUTES.ADMIN_PROJECT_NOTIFICATIONS.path, typedData.projectId.toString());
		}

		case "project-business": {
			const typedData = data as IGetLinkData["project-business"];

			return getProjectPath(ROUTES.ADMIN_PROJECT_BUSINESS.path, typedData.projectId.toString());
		}

		case "project-catalog": {
			const source = isInternal ? ROUTES.ADMIN_PROJECT_CATALOG.path : ROUTES.CLIENT_PROJECT_CATALOG.path;
			const typedData = data as IGetLinkData["project-catalog"];

			return getProjectPath(source, typedData.projectId.toString());
		}

		default:
			return "";
	}
}

export interface IItem {
	text: string;
	url: string;
	icon: ReactNode;
	opts: ReactNode;
	type: "link" | "external" | "separator";
}

function getAppendix(trans: TTrans, useSeparator?: boolean) {
	return [
		...useSeparator
			? [{
				text: "",
				url: "separator-documentation",
				icon: null,
				opts: null,
				type: "separator",
			}]
			: [],
		{
			text: trans("menu.help"),
			url: DOCUMENTATION_MENU_LINK,
			icon: <DocumentationIcon />,
			opts: <ExternalIcon />,
			type: "external",
		},
		{
			text: trans("menu.contact"),
			url: CONTACT_LINK,
			icon: <ContactPageIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: <ExternalIcon />,
			type: "external",
		},
	] as Array<IItem>;
}

export function getLinks(trans: TTrans, userData: IUserState, projectId: number, keyId: number, checkInternalRole: TInternalUserRole) {
	const items: Array<IItem> = [];
	const logged = userData.email !== "";
	const rootMenu = projectId === 0 && keyId === 0;
	const isInternal = checkInternalRole !== "none";
	const showMyProjects = logged && rootMenu && (userData.hasProjects || userData.internalRole === "none");

	if (TRANSFER_VERSION) {
		return getAppendix(trans);
	}

	if (showMyProjects) {
		items.push({
			text: trans("my.projects"),
			url: getLink("projects"),
			icon: <HomeIcon />,
			opts: null,
			type: "link",
		});
	}

	// upravy pro projekty
	if (logged && projectId > 0 && keyId === 0) {
		items.push({
			text: trans("menu.dashboard"),
			url: getLink("project-dashboard", isInternal, { projectId }),
			icon: <DashboardIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: null,
			type: "link",
		});

		if (userData.internalRole !== "none") {
			items.push({
				text: trans("menu.business"),
				url: getLink("project-business", isInternal, { projectId }),
				icon: <StoreIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
		}

		items.push({
			text: trans("apiKeys"),
			url: getLink("project-keys", isInternal, { projectId }),
			icon: <KeyIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: null,
			type: "link",
		});
		items.push({
			text: trans("menu.consumption"),
			url: getLink("project-consumption-history", isInternal, { projectId }),
			icon: <GraphIcon />,
			opts: null,
			type: "link",
		});
		items.push({
			text: trans("menu.financial"),
			url: getLink("project-financial", isInternal, { projectId }),
			icon: <PaymentIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: null,
			type: "link",
		});
		items.push({
			text: trans("projectSettingsPage.title"),
			url: getLink("project-settings", isInternal, { projectId }),
			icon: <SettingsIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: null,
			type: "link",
		});
		items.push({
			text: trans("menu.users"),
			url: getLink("project-users", isInternal, { projectId }),
			icon: <UsersIcon />,
			opts: null,
			type: "link",
		});
		items.push({
			text: trans("menu.catalog2"),
			url: getLink("project-catalog", isInternal, { projectId }),
			icon: <FilterIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
			opts: null,
			type: "link",
		});

		if (userData.internalRole !== "none") {
			items.push({
				text: trans("menu.adminHistory"),
				url: getLink("project-history", isInternal, { projectId }),
				icon: <HistoryIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: trans("menu.notificationsHistory"),
				url: getLink("project-notifications", isInternal, { projectId }),
				icon: <CircleNotificationsIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
		}

		items.push(
			...getAppendix(trans, true),
		);
	}

	// interni administrace
	if (logged && rootMenu && userData.isInternal) {
		if (userData.internalRole !== "none") {
			showMyProjects && items.push({
				text: "",
				url: "separator-admin",
				icon: null,
				opts: null,
				type: "separator",
			});
			items.push({
				text: trans("menu.adminProjects"),
				url: getLink("admin-projects"),
				icon: <FormatListBulletedIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});

			if (userData.internalRole === "internal_admin") {
				items.push({
					text: trans("menu.adminServices"),
					url: getLink("admin-services"),
					icon: <LanIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
					opts: null,
					type: "link",
				});
				items.push({
					text: trans("menu.adminInternalUsers"),
					url: getLink("admin-users"),
					icon: <UsersIcon />,
					opts: null,
					type: "link",
				});
				items.push({
					text: trans("menu.adminTransactions"),
					url: getLink("admin-transactions"),
					icon: <ReceiptIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
					opts: null,
					type: "link",
				});
			}

			items.push({
				text: trans("menu.adminReports2"),
				url: getLink("admin-reports"),
				icon: <ReportIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: trans("menu.adminGraphReports"),
				url: getLink("admin-graph-reports"),
				icon: <BarChartIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: "Billing Export",
				url: getLink("admin-billing-export"),
				icon: <ReceiptIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: trans("menu.adminCatalogs"),
				url: getLink("admin-catalogs"),
				icon: <FilterIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: trans("menu.adminQuickComment"),
				url: getLink("admin-quick-comment"),
				icon: <QuickreplyIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
			items.push({
				text: trans("menu.adminApiUsers"),
				url: getLink("admin-api-users"),
				icon: <ManageAccountsIcon sx={{ fill: "var(--dark-green-text-color)" }} />,
				opts: null,
				type: "link",
			});
		}
	}

	if (rootMenu) {
		items.push(
			...getAppendix(trans, items.length > 0),
		);
	}

	return items;
}
